// ============================================================
// JWFL Sound Engine
//   - Background music: 7 NCS tracks (the official JWFL soundtrack drop).
//   - SFX: procedural Web Audio for instant, light-weight feedback.
// ============================================================

const TRACKLIST = [
  { file: 'ascence_-_konnichiwa_.mp3',                                  artist: 'Ascence',                                       title: 'Konnichiwa',              genre: 'house' },
  { file: 'diviners_ren_-_savannah_2026_(japanese_version).mp3',        artist: 'Diviners & Ren',                                title: 'Savannah 2026 (JP ver.)', genre: 'electronic' },
  { file: 'mupp_-_x2.mp3',                                              artist: 'MUPP',                                          title: 'X2',                      genre: 'trap' },
  { file: 'rival_alan_walker_-_dreamer_(rival_remix).mp3',              artist: 'Rival & Alan Walker',                           title: 'Dreamer (Rival Remix)',   genre: 'house' },
  { file: 'twisted_stm_kellapsage_glossier_-_isoria.mp3',               artist: 'TWISTED, STM, kellapsage, glossier',            title: 'ISORIA',                  genre: 'electronic' },
  { file: 'warriyo_lxngvx_-_mortals_funk_remix_(slowed).mp3',           artist: 'Warriyo & LXNGVX',                              title: 'Mortals (Funk Slowed)',   genre: 'funk' },
  { file: 'cape_crysstales_-_com_bota.mp3',                             artist: 'cape & crysstales',                             title: 'COM BOTA',                genre: 'latin' },
];

const BASE_VOLUME = 0.45; // pleasant background level; mute toggles to 0

// ---------- State ----------
let audioCtx = null;       // shared Web Audio context for SFX
let sfxGain = null;
let audioEl = null;        // HTMLAudioElement playing the current track
let order = [];            // shuffled indexes for playback
let orderIdx = 0;
let currentTrack = null;
let trackChangeListeners = [];
let muted = false;
let fadeRaf = null;
let started = false;

try { muted = localStorage.getItem('jwfl-audio-muted') === '1'; } catch { muted = false; }

// ---------- Utilities ----------
const audioUrl = (file) => `${process.env.PUBLIC_URL || ''}/audio/${file}`;

const shuffle = (arr) => {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

const ensureSfxContext = () => {
  if (audioCtx) return audioCtx;
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return null;
  audioCtx = new Ctx();
  sfxGain = audioCtx.createGain();
  sfxGain.gain.value = muted ? 0 : 0.55;
  sfxGain.connect(audioCtx.destination);
  return audioCtx;
};

const now = () => audioCtx?.currentTime || 0;

const notifyTrackChange = () => {
  const payload = currentTrack ? { ...currentTrack } : null;
  trackChangeListeners.forEach(cb => { try { cb(payload); } catch { /* noop */ } });
};

const cancelFade = () => { if (fadeRaf) { cancelAnimationFrame(fadeRaf); fadeRaf = null; } };

const fadeTo = (targetVol, durationMs, onDone) => {
  if (!audioEl) { onDone?.(); return; }
  cancelFade();
  const start = audioEl.volume;
  const t0 = performance.now();
  const step = (t) => {
    const k = Math.min(1, (t - t0) / durationMs);
    const v = start + (targetVol - start) * k;
    audioEl.volume = Math.max(0, Math.min(1, v));
    if (k < 1) fadeRaf = requestAnimationFrame(step);
    else { fadeRaf = null; onDone?.(); }
  };
  fadeRaf = requestAnimationFrame(step);
};

// ---------- Mute ----------
export const setMuted = (val) => {
  muted = !!val;
  try { localStorage.setItem('jwfl-audio-muted', muted ? '1' : '0'); } catch { /* noop */ }
  if (sfxGain && audioCtx) {
    const t = now();
    sfxGain.gain.cancelScheduledValues(t);
    sfxGain.gain.linearRampToValueAtTime(muted ? 0 : 0.55, t + 0.25);
  }
  if (audioEl) {
    if (muted) fadeTo(0, 300);
    else fadeTo(BASE_VOLUME, 400);
  }
};
export const isMuted = () => muted;

// ---------- Background music ----------
const loadAndPlayCurrent = () => {
  if (!audioEl) {
    audioEl = new Audio();
    audioEl.preload = 'auto';
    audioEl.crossOrigin = 'anonymous';
    audioEl.addEventListener('ended', () => { advance(); });
    audioEl.addEventListener('error', () => { advance(); }); // skip broken files
  }
  const trackIdx = order[orderIdx % order.length];
  const meta = TRACKLIST[trackIdx];
  currentTrack = { ...meta };
  audioEl.src = audioUrl(meta.file);
  audioEl.volume = muted ? 0 : 0.0001;
  const p = audioEl.play();
  if (p && typeof p.then === 'function') p.catch(() => { /* autoplay blocked */ });
  if (!muted) fadeTo(BASE_VOLUME, 1200);
  notifyTrackChange();
};

const advance = () => {
  orderIdx = (orderIdx + 1) % order.length;
  // Re-shuffle once we've cycled the whole list so order varies each loop
  if (orderIdx === 0) order = shuffle(order);
  loadAndPlayCurrent();
};

export const startAmbientMusic = () => {
  if (started) return;
  started = true;
  if (order.length === 0) order = shuffle(TRACKLIST.map((_, i) => i));
  orderIdx = 0;
  loadAndPlayCurrent();
};

export const nextTrack = () => {
  if (!started) return;
  // Fade out the current, then advance and let the new one fade in
  fadeTo(0, 250, () => advance());
};

export const stopAmbientMusic = () => {
  if (!audioEl) { started = false; currentTrack = null; notifyTrackChange(); return; }
  fadeTo(0, 800, () => {
    try { audioEl.pause(); } catch { /* noop */ }
    started = false;
    currentTrack = null;
    notifyTrackChange();
  });
};

export const getCurrentTrack = () => (currentTrack ? { ...currentTrack } : null);
export const onTrackChange = (cb) => {
  trackChangeListeners.push(cb);
  return () => { trackChangeListeners = trackChangeListeners.filter(c => c !== cb); };
};
export const getTracklist = () => TRACKLIST.map(t => ({ ...t }));

// ============================================================
// SFX — kept procedural so they're instant + don't need extra files.
// ============================================================
const playOsc = (freq, type, t0, duration, volume = 0.15, detune = 0, atk = 0.04) => {
  if (!audioCtx) return;
  const osc = audioCtx.createOscillator();
  const g = audioCtx.createGain();
  osc.type = type;
  osc.frequency.value = freq;
  osc.detune.value = detune;
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(volume, t0 + atk);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + duration);
  osc.connect(g);
  g.connect(sfxGain);
  osc.start(t0);
  osc.stop(t0 + duration + 0.05);
};
const playKick = (t0, vol = 0.6) => {
  if (!audioCtx) return;
  const osc = audioCtx.createOscillator();
  const g = audioCtx.createGain();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(150, t0);
  osc.frequency.exponentialRampToValueAtTime(38, t0 + 0.2);
  g.gain.setValueAtTime(vol, t0);
  g.gain.exponentialRampToValueAtTime(0.001, t0 + 0.35);
  osc.connect(g); g.connect(sfxGain);
  osc.start(t0); osc.stop(t0 + 0.38);
};
const midi = (n) => 440 * Math.pow(2, (n - 69) / 12);

export const playIntroSting = () => {
  if (!ensureSfxContext()) return;
  if (audioCtx.state === 'suspended') audioCtx.resume().catch(() => {});
  const t0 = now();
  [midi(36), midi(43), midi(51), midi(60)].forEach((f, i) =>
    playOsc(f, i < 2 ? 'sawtooth' : 'triangle', t0 + i * 0.18, 2.4, 0.18, (Math.random() - 0.5) * 8)
  );
  playKick(t0 + 2.1);
};

export const playUiClick = () => {
  if (!ensureSfxContext() || muted) return;
  if (audioCtx.state === 'suspended') audioCtx.resume().catch(() => {});
  const t = now();
  const osc = audioCtx.createOscillator(); const g = audioCtx.createGain();
  osc.type = 'square'; osc.frequency.setValueAtTime(1400, t);
  osc.frequency.exponentialRampToValueAtTime(700, t + 0.06);
  g.gain.setValueAtTime(0.10, t); g.gain.exponentialRampToValueAtTime(0.0001, t + 0.09);
  osc.connect(g); g.connect(sfxGain); osc.start(t); osc.stop(t + 0.1);
};

export const playGoal = () => {
  if (!ensureSfxContext() || muted) return;
  if (audioCtx.state === 'suspended') audioCtx.resume().catch(() => {});
  const t = now();
  [523.25, 659.25, 783.99, 1046.50].forEach((freq, i) => {
    const osc = audioCtx.createOscillator(); const g = audioCtx.createGain();
    osc.type = 'triangle'; osc.frequency.value = freq;
    g.gain.setValueAtTime(0.0001, t + i * 0.08);
    g.gain.exponentialRampToValueAtTime(0.25, t + i * 0.08 + 0.04);
    g.gain.exponentialRampToValueAtTime(0.0001, t + i * 0.08 + 0.45);
    osc.connect(g); g.connect(sfxGain);
    osc.start(t + i * 0.08); osc.stop(t + i * 0.08 + 0.5);
  });
};
